/**
 * Reports recovery outcomes.
 *
 * Mixpanel in prod (it only initialises there — see instrumentation-client.ts),
 * console elsewhere when debug mode is on. Payloads are fixed-size scalars only:
 * no URLs, symbols, account ids or anything else that could carry user or desk
 * information.
 */
import { env } from '@/env'
import { track } from '@/lib/analytics'
import store from '@/lib/configs/store-config'
import { debugModeAtom } from '@/state/debug'

import { TELEMETRY_MIN_INTERVAL_MS } from './health-policy'
import type { RecoveryLevel, RecoveryReason, RestartResult, ResyncAckResult } from './types'

const EVENT_NAME = 'runtime_recovery' as const

export type RecoveryTelemetryPayload = {
  // Environment
  userAgentBrand: string
  platform: string
  deviceMemoryGb: number | null

  // Lifecycle
  reasons: RecoveryReason[]
  hiddenDurationMs: number
  executionGapMs: number
  wasRestoredFromBFCache: boolean
  wasDiscarded: boolean

  // Memory
  memoryApiSupported: boolean
  heapUsageRatio: number | null

  // UI responsiveness
  lagApiSupported: boolean
  lagEntryType: string | null
  lagSampleCount: number
  lagTotalDurationMs: number
  lagMaxDurationMs: number
  lagSevereCount: number

  // Socket
  socketIsReady: boolean
  transportAgeMs: number | null
  heartbeatAgeMs: number | null
  dataAgeMs: number | null
  reconnectCount: number
  activeSubscriptionCount: number

  // App resources
  totalQueryCount: number
  inactiveQueryCount: number
  configuredWidgetCount: number
  mountedWidgetCount: number
  tradingViewContainerCount: number

  // Query invalidation cost — the whole point of keeping the unfiltered
  // invalidateQueries() for now is being able to measure what it costs before
  // narrowing it.
  activeQueriesBeforeInvalidate: number
  queriesFetchingAfterInvalidate: number
  invalidateDurationMs: number
  failedRefetchCount: number

  // Level 2
  level2Executed: boolean
  removedInactiveQueries: number | null
  marketDataKeysFlushed: number | null

  // Outcome
  score: number
  executedLevel: RecoveryLevel
  recommendedLevel: RecoveryLevel
  shadowLevel: RecoveryLevel | null
  /**
   * Signals alone would put this in Level 3 territory. NOT "Level 3 was needed"
   * — with Level 2 gated off we never learn whether Level 2 would have fixed it.
   */
  level3Candidate: boolean
  restartResult: RestartResult | null
  ackResult: ResyncAckResult | null
  level1DurationMs: number | null
  totalDurationMs: number
  succeeded: boolean
  safeToReload: boolean
  reloadBlockers: string[]
  assessmentReasons: string[]
}

const lastSentAtByLevel = new Map<RecoveryLevel, number>()

export type Level3Outcome =
  | 'blocked-cooldown'
  | 'blocked-unsafe'
  | 'blocked-backend-stream'
  | 'reloaded'

/**
 * Separate, lightweight event for the Level 3 decision. Kept apart from
 * reportRecovery because whether it actually reloaded is decided inside
 * runLevel3, after the main recovery report already went out, and re-shaping
 * the full payload just to attach one more field isn't worth the coupling.
 */
export function reportLevel3Outcome(outcome: Level3Outcome): void {
  const payload = { outcome, at: Date.now() }

  if (env.NEXT_PUBLIC_ENV === 'prod') {
    void track('runtime_recovery_level3', payload)
    return
  }

  if (store.get(debugModeAtom)) {
    console.info('[runtime-recovery] level3', payload)
  }
}

/** Coarse brand string; avoids shipping the full UA. */
function readUserAgentBrand(): string {
  if (typeof navigator === 'undefined') return 'unknown'
  const brands = (navigator as Navigator & { userAgentData?: { brands?: { brand: string }[] } })
    .userAgentData?.brands
  if (brands && brands.length > 0) {
    return brands.map((b) => b.brand).join('|')
  }
  const ua = navigator.userAgent
  if (ua.includes('Firefox')) return 'Firefox'
  if (ua.includes('Edg/')) return 'Edge'
  if (ua.includes('Chrome')) return 'Chrome'
  if (ua.includes('Safari')) return 'Safari'
  return 'unknown'
}

export function readPlatform(): string {
  if (typeof navigator === 'undefined') return 'unknown'
  return (
    (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform ??
    navigator.platform ??
    'unknown'
  )
}

export function environmentFields(deviceMemoryGb: number | null) {
  return {
    userAgentBrand: readUserAgentBrand(),
    platform: readPlatform(),
    deviceMemoryGb,
  }
}

/**
 * Send one recovery report.
 *
 * Rate-limited per level so a flapping connection can't produce a storm of
 * identical events. `force` bypasses that for outcomes worth always keeping.
 */
export function reportRecovery(payload: RecoveryTelemetryPayload, force = false): void {
  const now = Date.now()
  const lastSentAt = lastSentAtByLevel.get(payload.executedLevel) ?? 0
  if (!force && now - lastSentAt < TELEMETRY_MIN_INTERVAL_MS) return
  lastSentAtByLevel.set(payload.executedLevel, now)

  if (env.NEXT_PUBLIC_ENV === 'prod') {
    // lib/analytics imports mixpanel-browser lazily and swallows errors, so this
    // is safe in non-browser environments and can never break recovery.
    void track(EVENT_NAME, payload)
    return
  }

  if (store.get(debugModeAtom)) {
    console.info(`[runtime-recovery] ${EVENT_NAME}`, payload)
  }
}

/** Test seam. */
export function resetTelemetryRateLimitForTest(): void {
  lastSentAtByLevel.clear()
}
